import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { fetchData } from "../../api/api";
import { useAppConfig } from "../../context/AppConfigContext";
import { useAuth } from "../../context/AuthContext";
import ShareSection from "./ShareSection";
import styles from "./Referral.module.css";

const REFERRAL_TIERS = [
  { key: "silver", title: "Premium Silver", target: 7 },
  { key: "gold", title: "Premium Gold", target: 15 },
  { key: "allinclusive", title: "All Inclusive", target: 30 },
];

const getNextReferralTier = (count) =>
  REFERRAL_TIERS.find((tier) => count < tier.target) || null;

const Referral = ({ userId, platform }) => {
  const { t, site, brandName } = useAppConfig();
  const { isGuest } = useAuth();
  const [refCode, setRefCode] = useState("");
  const [referralLink, setReferralLink] = useState("");
  const [referralCount, setReferralCount] = useState(0);
  const [earningsTotal, setEarningsTotal] = useState(0);
  const [earningsCurrency, setEarningsCurrency] = useState("USD");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const isNative = Capacitor.isNativePlatform();
  const fallbackReferralLink = refCode
    ? `${site}/invite/${refCode}`
    : "";
  const activeReferralLink = referralLink || fallbackReferralLink;
  const nextTier = getNextReferralTier(referralCount);
  const progressTarget = nextTier?.target || referralCount || 1;
  const progressPercent = Math.min((referralCount / progressTarget) * 100, 100);

  useEffect(() => {
    if (isGuest) {
      setLoading(false);
      return;
    }

    const loadReferralLink = async () => {
      try {
        const res = await fetchData("/api/referrals", "POST", { userId });
        setRefCode(res.refCode || "");
        setReferralLink(res.referralLink || "");
        setReferralCount(
          Number.isFinite(res.referralCount)
            ? res.referralCount
            : Array.isArray(res.referrals)
              ? res.referrals.length
              : 0,
        );
        setEarningsTotal(Number(res.earningsTotal) || 0);
        setEarningsCurrency(res.earningsCurrency || "USD");
        setMessage("");
      } catch (error) {
        setMessage(t.referrals?.error || "Error loading referrals");
      } finally {
        setLoading(false);
      }
    };

    loadReferralLink();
  }, [isGuest, t.referrals, userId]);

  return (
    <section className={styles.referralBlock}>
      <div className={styles.header}>
        <h2>{t.referrals?.title || "Referral Program"}</h2>
        <p
          className={styles.description}
          dangerouslySetInnerHTML={{
            __html:
              t.referrals?.promo ||
              `Invite friends and get rewarded when they join ${brandName}.`,
          }}
        />
      </div>

      {isGuest ? (
        <p className={styles.tempWarning}>
          {t.referrals?.temp_warning ||
            "Create a full account to unlock your referral link."}
        </p>
      ) : loading ? (
        <p className={styles.statusText}>
          {t.referrals?.loading || "Loading..."}
        </p>
      ) : (
        <>
          <div className={styles.earningsCard}>
            <p className={styles.earningsLabel}>
              {t.referrals?.earned_label || "You earned"}
            </p>
            <p className={styles.earningsValue}>
              {earningsTotal.toFixed(2)} {earningsCurrency}
            </p>
          </div>
          <ShareSection
            platform={platform}
            referralLink={activeReferralLink}
            setMessage={setMessage}
            isNative={isNative}
          />
          {nextTier ? (
            <div className={styles.progressCard}>
              <p className={styles.progressLabel}>
                {t.referrals?.progress_title || "Your progress"}
              </p>
              <h3 className={styles.progressTitle}>
                {t.premium?.[nextTier.key]?.title || nextTier.title}
              </h3>
              <div className={styles.progressBar} aria-hidden="true">
                <div
                  className={styles.progressFill}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className={styles.progressMeta}>
                {referralCount} / {nextTier.target}{" "}
                {t.referrals?.friends_label || "friends"}
              </p>
            </div>
          ) : (
            <div className={styles.progressCard}>
              <p className={styles.progressLabel}>
                {t.referrals?.progress_title || "Your progress"}
              </p>
              <h3 className={styles.progressTitle}>
                {t.referrals?.max_level_title || "All referral levels unlocked"}
              </h3>
            </div>
          )}
        </>
      )}

      {message ? <p className={styles.message}>{message}</p> : null}
    </section>
  );
};

export default Referral;

